import { FeeStatistics, NominalAccount, NominalAccountBankAccountsForBeneficiary, PlatformDetails } from "interfaces";
import { makeAutoObservable } from "mobx";
import {
  cancelRubWithdrawalRequest,
  completeRubWithdrawalRequest,
  getNominalAccountsUserBalance,
  getNominalAccountsOverallBalance,
  startProcessingRubWithdrawalRequest,
  rubOperationsDepositRequest,
  getPlatformDetailsRequest,
  getFeeStatistics,
  getNominalAccountBankAccountsForBeneficiary,
} from "services";
import { fetchStatus, withRequestTracking } from "utils/storesUtils";
import { DataStoreConstructor } from "./core/DataStoreConstructor";
import { IStores } from "stores";

type Params = {
  rubOperationsFetch?: boolean;
  rubWithdrawalRequestsFetch?: boolean;
  userBalancesFetch?: boolean;
};

const defaultFeeStatistics = {
  totalFee: 0,
  monthFee: 0,
  dayFee: 0,
};

const defaultNominalAccount: NominalAccount = {
  companyId: "",
  totalBalance: 0,
  availableBalance: 0,
  blockedOnRequests: 0,
  blockedForWithdrawal: 0,
};

const defaultPlatformDetails: PlatformDetails = {
  companyName: "",
  companyRegistrationNumber: "",
  inn: "",
  kpp: "",
  defaultAccount: {
    bankName: "",
    bik: "",
    accountNumber: "",
    correspondentAccount: "",
    id: "",
    accountName: "",
    bankRegistrationNumber: "",
    isDefault: false,
    isArchived: false,
  },
  paymentPurpose: "",
};

const defaultNominalAccountBankAccounts: NominalAccountBankAccountsForBeneficiary[] = [
  {
    bik: "",
    correspondentAccount: "",
    bankName: "",
    isDefault: false,
    accountNumber: "",
  },
];

const defaultStateStatus = { loading: false, fetchStatus: fetchStatus.idle, fetchError: undefined };

export class NominalAccountsStore {
  stores: IStores;

  userBalances: DataStoreConstructor<NominalAccount> = new DataStoreConstructor({
    fetchMethod: getNominalAccountsUserBalance,
    defaultData: defaultNominalAccount,
  });

  overallBalances: DataStoreConstructor<NominalAccount> = new DataStoreConstructor({
    fetchMethod: getNominalAccountsOverallBalance,
    defaultData: defaultNominalAccount,
  });

  platformDetails: DataStoreConstructor<PlatformDetails> = new DataStoreConstructor({
    fetchMethod: getPlatformDetailsRequest,
    defaultData: defaultPlatformDetails,
  });

  feeStatistics: DataStoreConstructor<FeeStatistics> = new DataStoreConstructor({
    fetchMethod: getFeeStatistics,
    defaultData: defaultFeeStatistics,
  });

  startProcessingStatuses = { ...defaultStateStatus };
  completeStatuses = { ...defaultStateStatus };
  cancelStatuses = { ...defaultStateStatus };
  depositRequestStatuses = { ...defaultStateStatus };
  platformDetailsStatuses = { ...defaultStateStatus };
  feeStatisticsStatuses = { ...defaultStateStatus };
  nominalAccountBankAccountsStatuses = { ...defaultStateStatus };

  nominalAccountBankAccountsForBeneficiary: NominalAccountBankAccountsForBeneficiary[] = [
    ...defaultNominalAccountBankAccounts,
  ];

  constructor(stores: IStores) {
    this.stores = stores;
    makeAutoObservable(this);
  }

  async fetchAllBalances({
    rubOperationsFetch = true,
    rubWithdrawalRequestsFetch = true,
    userBalancesFetch = true,
  }: Params = {}) {
    return await Promise.all(
      [
        rubOperationsFetch && this.stores.rubOperations.fetch(),
        rubWithdrawalRequestsFetch && this.stores.rubWithdrawalRequests.fetch(),
        userBalancesFetch && this.userBalances.fetch(),
        this.stores.user.roles.isOperator && this.overallBalances.fetch(),
      ].filter(Boolean)
    );
  }

  async fetchStartProcessingRubWithdrawal(bidId: string) {
    return withRequestTracking(this.startProcessingStatuses, async () => {
      return await startProcessingRubWithdrawalRequest(bidId);
    });
  }

  async fetchCompleteRubWithdrawal(bidId: string, formData: any) {
    return withRequestTracking(this.completeStatuses, async () => {
      return await completeRubWithdrawalRequest(bidId, formData);
    });
  }

  async fetchCancelRubWithdrawal(bidId: string) {
    return withRequestTracking(this.completeStatuses, async () => {
      return await cancelRubWithdrawalRequest(bidId);
    });
  }

  async fetchRubDepositOperation(formData: any) {
    return withRequestTracking(this.depositRequestStatuses, async () => {
      return await rubOperationsDepositRequest(formData);
    });
  }

  async fetchFeeStatistics() {
    return withRequestTracking(this.feeStatistics.statuses, async () => {
      return await getFeeStatistics();
    });
  }

  async fetchNominalAccountBankAccountsForBeneficiary(id: string) {
    return withRequestTracking(this.nominalAccountBankAccountsStatuses, async () => {
      const response = await getNominalAccountBankAccountsForBeneficiary(id);
      this.nominalAccountBankAccountsForBeneficiary = response;
      return response;
    });
  }

  clear() {
    this.userBalances.clear();
    this.overallBalances.clear();
  }
}
